import { useEffect, useRef, useState } from 'react';
import { Download, RotateCcw } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { RunReportContext } from '@/reports/types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

interface BundleContextPanelProps {
  context: RunReportContext;
  /** the author typed a context by hand */
  onChange: (context: RunReportContext) => void;
  /** read a real run as the draft reads it; returns why it could not, or null */
  onLoadRun: (runId: string) => Promise<string | null>;
  onReset: () => void;
}

/**
 * What the preview hands the report: the sample context, a real run's — read
 * as the saved draft reads it, with the point files its traces ask for — or
 * whatever the author types.
 */
export function BundleContextPanel({ context, onChange, onLoadRun, onReset }: BundleContextPanelProps) {
  const [text, setText] = useState(() => JSON.stringify(context, null, 2));
  const [error, setError] = useState<string | null>(null);
  const [runId, setRunId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** the context this panel last handed up, so one arriving from elsewhere (a loaded run, the assistant) replaces the text */
  const emitted = useRef<RunReportContext | null>(null);

  useEffect(() => {
    if (context === emitted.current) return;
    setText(JSON.stringify(context, null, 2));
    setError(null);
  }, [context]);

  const handleText = (value: string) => {
    setText(value);
    try {
      const parsed = JSON.parse(value) as RunReportContext;
      setError(null);
      emitted.current = parsed;
      onChange(parsed);
    } catch {
      setError(I18n.get('reportBundleContextInvalid'));
    }
  };

  const loadRun = async () => {
    if (!runId.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      setLoadError(await onLoadRun(runId.trim()));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 p-[1rem]">
      <p className="max-w-prose text-xs text-muted-foreground">{I18n.get('reportBundlePreviewContextHelp')}</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid min-w-48 flex-1 gap-1.5">
          <Label htmlFor="preview-run-id">{I18n.get('reportBundleRunId')}</Label>
          <Input id="preview-run-id" className="font-mono" value={runId} onChange={(e) => setRunId(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void loadRun(); }} />
        </div>
        <Button variant="outline" onClick={() => void loadRun()} disabled={loading || !runId.trim()}><Download /> {I18n.get('reportBundleLoadRun')}</Button>
        <Button variant="ghost" onClick={onReset}><RotateCcw /> {I18n.get('reportBundleResetContext')}</Button>
      </div>
      {loadError && <p className="text-xs text-destructive">{loadError}</p>}
      <Textarea
        className="min-h-[60vh] flex-1 resize-y font-mono text-xs md:text-xs field-sizing-fixed"
        value={text}
        aria-label={I18n.get('reportBundlePreviewContext')}
        aria-invalid={error !== null}
        onChange={(e) => handleText(e.target.value)}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
